import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../../components/loader";
import { openPopUp } from "../../../components/popUp";
import UnloadBlocker from "../../../components/unloadBlocker";
import { useDashboardContext } from "../../../context/dashboardContext";
import { getApiUrl } from "../../../data/api/apiManager";
import { convertPieceResponseDTOToPieceDTO } from "../../../data/pieceManager";
import { RawScoreData } from "../../../data/scoreData";
import { PerformanceDTOTypeEnum, PieceDTO, PieceResponseDTO, ScoreResponseDTO, ScoreUpdateDTO } from "../../../openapi-generated-client";
import { deepObjectEqual } from "../../../utils/objectUtils";
import DashboardEditPieceWrapper from "./dashboardEditPieceWrapper";
import PieceSummaryPopUp from "./pieceSummaryPopUp";
import EditPieceContributors from "./steps/editPieceContributors";
import EditPieceGeneral from "./steps/editPieceGeneral";
import EditPieceInstrumenation from "./steps/editPieceInstrumentation";
import EditPiecePerformances from "./steps/editPiecePerformances";
import EditPieceScores from "./steps/editPieceScores";
import EditPiecePreview from "./steps/editPiecePreview";

interface EditPieceStepProps {
    piece: PieceDTO;
    setPiece: (piece: PieceDTO) => void;
    setStepMessage: (v: string) => void;
}

interface DashboardEditPieceProps {
    defaultPiece: PieceDTO;
    onUpdate: (piece: PieceDTO) => Promise<string>;
}

export default function DashboardEditPiece(props: DashboardEditPieceProps) {

    const {sessionId, appContext} = useDashboardContext();
    const {scoreController} = appContext;

    const [defaultScores, setDefaultScores] = useState<RawScoreData[]>([]);
    const [defaultPiece, setDefaultPiece] = useState<PieceDTO>(props.defaultPiece);

    const [showStepMessage, setShowStepMessage] = useState(false);

    const [piece, setPiece] = useState<PieceDTO>(props.defaultPiece);
    const [scores, setScores] = useState<RawScoreData[]>([]);
    const [audios, setAudios] = useState<File[]>([]);
    const [step, setStep] = useState(1);
    const [stepMessage, setStepMessage] = useState("");

    function updatePiece(v: PieceDTO) {
        setPiece(v);
        setShowStepMessage(false);
    }

    function updateScores(v: RawScoreData[]) {
        setScores(v);
        setShowStepMessage(false);
    }

    useEffect(() => {
        setDefaultPiece(props.defaultPiece);
        setPiece(props.defaultPiece);
        const newScores = [];
        if(!props.defaultPiece.scores) return;
        for(var i = 0; i < props.defaultPiece.scores.length; i++) {
            var current = props.defaultPiece.scores[i];
            newScores.push(new RawScoreData(current.name, current.name, undefined, current.id, current.fileHash));
        }
        setScores(newScores);
        setDefaultScores(newScores);
    }, [props.defaultPiece]);

    return <DashboardEditPieceWrapper onPublish={() => {
        if(stepMessage.length != 0) {
            setShowStepMessage(true);
            return
        }
        openPopUp(<PieceSummaryPopUp scores={scores} piece={piece} onPublish={async () => {
            var newPiece = {...piece};
            var newScores: ScoreUpdateDTO[] = [];
                console.log(scores);
            for(var i = 0; i < scores.length; i++) {
                var currentScore = await uploadScore(scores[i], sessionId);
                newScores.push({id: currentScore?.id, name: currentScore?.name} as ScoreUpdateDTO);
            }
            newPiece.scores = newScores;
            // Setting all variables to the new piece, to disable unload blocking
            setDefaultPiece(newPiece);
            setPiece(newPiece);
            setScores(scores);
            setDefaultScores(scores);
            return props.onUpdate(newPiece);
        }}/>);
    }} showError={showStepMessage} nextStepDisabled={stepMessage != ""} onPrevStep={() => {
        setStep(step - 1);
        setStepMessage("");
    }} onNextStep={() => {
        if(stepMessage.length == 0) {
            setStep(step + 1);
        }else {
            setShowStepMessage(true);
        }
    }} error={stepMessage} totalSteps={6} step={step}>
        {step == 1 ? 
        <EditPieceScores setPiece={updatePiece} piece={piece} setStepMessage={setStepMessage} setScores={updateScores} scores={scores}/> : 
        step == 2 ? <EditPiecePreview  setPiece={updatePiece} piece={piece} setStepMessage={setStepMessage} setScores={updateScores} scores={scores}/> : 
        step == 3 ? <EditPieceGeneral setStepMessage={setStepMessage} setPiece={updatePiece} piece={piece}/> : 
        step == 4 ? <EditPieceInstrumenation setStepMessage={setStepMessage} setPiece={updatePiece} piece={piece}/> : 
        step == 5 ? <EditPieceContributors setStepMessage={setStepMessage} setPiece={updatePiece} piece={piece}/> : 
        step == 6 ? <EditPiecePerformances audios={audios} setAudios={setAudios} setStepMessage={setStepMessage} setPiece={updatePiece} piece={piece}/> : 
        <></>}
        <UnloadBlocker block={!deepObjectEqual(defaultPiece, piece) || !deepObjectEqual(defaultScores, scores)}/>
    </DashboardEditPieceWrapper>
}

export function DashboardEditPiecePage() {

    const {sessionId, appContext} = useDashboardContext();
    const {pieceController, scoreController} = appContext;

    const {id} = useParams();

    const [data, setData] = useState<PieceResponseDTO>();

    async function fetchData() {
        if(isNaN(Number(id))) {
            window.location.href = "/error/2";
            return;
        }
        setData((await pieceController.getPiece(Number(id), sessionId)).data)
    }

    useEffect(() => {
        fetchData();
    }, [id]);

    if (!data) return <Loader/>;
    return <DashboardEditPiece defaultPiece={convertPieceResponseDTOToPieceDTO(data)} onUpdate={async (piece: PieceDTO) => {
        try {
            await pieceController.updatePiece(sessionId, Number(id), piece);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : String(e);
            console.log("error uploading piece", errorMessage);
            return errorMessage;
        }
        fetchData();
        return "";
    }}/>

}


export type { EditPieceStepProps };



async function uploadScore(score: RawScoreData, sessionId: string) {
    var body = new FormData();
    console.log("uploading score", score);
    if(score.file == undefined) return {id: score.scoreId, name: score.name} as ScoreResponseDTO;
    body.append('scoreFile', score.file, score.name);
    body.append('scoreUpdateDTO', new Blob([
                JSON.stringify({
                    name: score.name
                })
            ], {
                type: "application/json"
            }));
    const response = await fetch(getApiUrl("score"), {
        method: "PUT",
        headers: {
            "authorization": sessionId
        },
        body: body
    });
    const data = await response.json();
    return data as ScoreResponseDTO;
}
export { uploadScore };
